/**
 * Owner: Masa Bayu
 * File: marketplace-search.ts
 * Date:Time: 2026-09-18 13:18
 * Description: T2/UI-06 search — q · facets · author filter · AuthorMatch groups.
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { assertOptionalTrimmed } from './guards';
import { createQxreadBookCard } from './marketplace-book-card';
import { QXREAD_CANONICAL_TOPIC_ID_PATTERN } from './global-taxonomy';
export const QXREAD_MARKETPLACE_SORTS = ['relevance', 'title_asc', 'title_desc', 'newest'];
export function createMarketplaceSearchQuery(options = {}) {
    assertNoForbiddenFoundationKey(options, 'QXRead marketplace search query');
    const sort = options.sort ?? 'relevance';
    if (!QXREAD_MARKETPLACE_SORTS.includes(sort)) {
        throw new Error(`QXRead marketplace sort must be one of: ${QXREAD_MARKETPLACE_SORTS.join(', ')}`);
    }
    let q = '';
    if (options.q !== undefined) {
        if (typeof options.q !== 'string') {
            throw new Error('QXRead search q must be a string');
        }
        q = options.q.trim();
    }
    const topicId = assertOptionalTrimmed(options.topicId, 'QXRead search topicId');
    if (topicId !== null && !QXREAD_CANONICAL_TOPIC_ID_PATTERN.test(topicId)) {
        throw new Error('QXRead search topicId must be a dotted canonical id');
    }
    return Object.freeze({
        q,
        category: assertOptionalTrimmed(options.category, 'QXRead search category'),
        tag: assertOptionalTrimmed(options.tag, 'QXRead search tag'),
        topicId,
        genreId: assertOptionalTrimmed(options.genreId, 'QXRead search genreId'),
        publicationTypeId: assertOptionalTrimmed(options.publicationTypeId, 'QXRead search publicationTypeId'),
        language: assertOptionalTrimmed(options.language, 'QXRead search language'),
        regionId: assertOptionalTrimmed(options.regionId, 'QXRead search regionId'),
        author: assertOptionalTrimmed(options.author, 'QXRead search author'),
        sort,
    });
}
function tagsOf(entry) {
    return Array.isArray(entry.metadata.tags) ? entry.metadata.tags : [];
}
function authorsOf(entry) {
    const raw = entry.metadata.authors;
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw.filter((name) => typeof name === 'string' && name.trim().length > 0);
}
function tagHaystack(entry) {
    return tagsOf(entry).join(' ').toLowerCase();
}
/** Demo fallback: match facet id against tags / language / loose id tokens. */
function matchesFacetToken(entry, token) {
    const needle = token.toLowerCase();
    const tags = tagsOf(entry).map((t) => t.toLowerCase());
    if (tags.includes(needle)) {
        return true;
    }
    const last = needle.includes('.') ? needle.slice(needle.lastIndexOf('.') + 1) : needle;
    if (tags.includes(last) || tags.some((t) => t.includes(last.replace(/_/g, '-')))) {
        return true;
    }
    const parts = last.split('_').filter((part) => part.length > 2);
    if (parts.some((part) => tags.includes(part) || tags.some((t) => t.includes(part)))) {
        return true;
    }
    const compact = last.replace(/_/g, ' ');
    return tagHaystack(entry).includes(compact) || entry.title.toLowerCase().includes(compact);
}
function entryMatchesAuthor(entry, author) {
    const needle = author.toLowerCase();
    return authorsOf(entry).some((name) => name.toLowerCase() === needle);
}
function matchesQuery(entry, query) {
    if (!entry.marketplaceReady) {
        return false;
    }
    if (query.category !== null && !tagsOf(entry).includes(query.category)) {
        return false;
    }
    if (query.tag !== null && !tagsOf(entry).includes(query.tag)) {
        return false;
    }
    if (query.topicId !== null && !matchesFacetToken(entry, query.topicId)) {
        return false;
    }
    if (query.genreId !== null && !matchesFacetToken(entry, query.genreId)) {
        return false;
    }
    if (query.publicationTypeId !== null && !matchesFacetToken(entry, query.publicationTypeId)) {
        return false;
    }
    if (query.regionId !== null && !matchesFacetToken(entry, query.regionId)) {
        return false;
    }
    if (query.language !== null) {
        const lang = entry.metadata.language;
        if (typeof lang !== 'string' || lang.toLowerCase() !== query.language.toLowerCase()) {
            return false;
        }
    }
    if (query.author !== null && !entryMatchesAuthor(entry, query.author)) {
        return false;
    }
    if (query.q.length === 0) {
        return true;
    }
    const needle = query.q.toLowerCase();
    const authors = authorsOf(entry).join(' ');
    const haystack = `${entry.title} ${authors} ${tagHaystack(entry)}`.toLowerCase();
    return haystack.includes(needle);
}
function sortEntries(entries, sort) {
    const copy = [...entries];
    if (sort === 'title_asc') {
        copy.sort((a, b) => a.title.localeCompare(b.title));
    }
    else if (sort === 'title_desc') {
        copy.sort((a, b) => b.title.localeCompare(a.title));
    }
    else if (sort === 'newest') {
        copy.reverse();
    }
    return copy;
}
const AUTHOR_MATCH_LIMIT = 6;
/**
 * Quiet AuthorMatch list when q or author is active.
 * Counts titles across marketplace-ready catalog · never invents people.
 */
export function listAuthorMatches(catalog, options = {}) {
    const query = createMarketplaceSearchQuery(options);
    if (query.q.length === 0 && query.author === null) {
        return Object.freeze([]);
    }
    const counts = new Map();
    for (const entry of catalog) {
        if (!entry.marketplaceReady) {
            continue;
        }
        for (const name of authorsOf(entry)) {
            const key = name.toLowerCase();
            if (query.author !== null && key !== query.author.toLowerCase()) {
                continue;
            }
            if (query.author === null && query.q.length > 0) {
                if (!key.includes(query.q.toLowerCase())) {
                    continue;
                }
            }
            const prev = counts.get(key);
            if (prev === undefined) {
                counts.set(key, { name, titleCount: 1 });
            }
            else {
                prev.titleCount += 1;
            }
        }
    }
    return Object.freeze([...counts.values()]
        .sort((a, b) => b.titleCount - a.titleCount || a.name.localeCompare(b.name))
        .slice(0, AUTHOR_MATCH_LIMIT)
        .map((row) => Object.freeze({ name: row.name, titleCount: row.titleCount })));
}
/** Search over catalog-safe serialized entries only — never content URLs. */
export function searchMarketplaceCatalog(catalog, options = {}) {
    const query = createMarketplaceSearchQuery(options);
    const matched = sortEntries(catalog.filter((entry) => matchesQuery(entry, query)), query.sort);
    return Object.freeze({
        query,
        total: matched.length,
        cards: Object.freeze(matched.map((entry) => createQxreadBookCard(entry))),
    });
}
//# sourceMappingURL=marketplace-search.js.map